export interface ErrorEntity {
  errorCode: string;
  errorMessage: string;
}

export interface BackEndError {
  message?: string;
  messageDetails?: string;
  errorCodes?: ErrorEntity[];
  status?: number;
}

export const managed_errors_codes = [
  "4.1.001",
  "4.2.001",
  "4.2.002",
  "4.2.003",
  "4.2.004",
  "4.5.001",
  "4.5.002",
  "6.1.001",
  "9.0.005",
  // The draft moved between the impact report and the publish. Handled where it
  // happens (publishWorkflow), which can say what to do about it and reload the
  // report - the generic snackbar cannot.
  "9.0.009",
];

/** The publish precondition failed: what was reviewed is not what would be published. */
export const WORKFLOW_DRAFT_CHANGED_CODE = "9.0.009";

/**
 * The draft is not publishable, e.g. a new or changed Specific-date Time Delay
 * whose date and time have already passed, or an invalid split (412). Split
 * stats and winner calls use it too, for a node that is not a published split.
 */
export const WORKFLOW_DEFINITION_INVALID_CODE = "9.0.008";

export function hasErrorCode(error: any, code: string): boolean {
  const errorCodes: ErrorEntity[] | undefined =
    error?.response?.data?.errorCodes;
  return !!errorCodes?.some((e) => e.errorCode === code);
}

export function isManagedError(error?: ErrorEntity[]): boolean {
  return (
    (error?.filter((e) => managed_errors_codes.includes(e.errorCode))?.length ||
      0) > 0
  );
}

export const DEFAULT_ERROR_MESSAGE = "Something Went wrong!";

export async function extractBackendError(response: {
  [key: string]: any;
}): Promise<BackEndError> {
  if (response?.data instanceof Blob) {
    return {
      ...JSON.parse(await response?.data.text()),
      status: response?.status,
    };
  } else {
    return { ...response?.data, status: response?.status };
  }
}

export function getErrorMessage(
  error: BackEndError,
  skipStatuses = [401]
): string {
  let message = DEFAULT_ERROR_MESSAGE;
  if (error?.status === 401 && skipStatuses.includes(error?.status)) {
    return "";
  } else if (error?.status === 412) {
    // The workflow service's `message` is prefixed with the exception's class
    // name ("InvalidDSLStructureException: ..."); the error code's own message
    // is the plain sentence.
    const definitionError = error.errorCodes?.find(
      (e) => e.errorCode === WORKFLOW_DEFINITION_INVALID_CODE
    )?.errorMessage;
    message = definitionError || error.message || message;
  }
  return message;
}

export class AppError extends Error {
  constructor(message: string) {
    super(message);
    this.message = message;
  }
}

export function extractErrorMessage(error: any, skipStatuses = [401]) {
  if (!error) {
    return;
  }
  if (error instanceof AppError) {
    return error.message;
  }
  if ("response" in error!) {
    const response = (error as any).response;
    const status = response?.status;
    const message = response?.data.message;
    const errorCodes = response?.data?.errorCodes;
    if (isManagedError(errorCodes) || skipStatuses.includes(error?.status)) {
      return undefined;
    }
    if (status === 412) {
      // Same preference as getErrorMessage: the error code's plain sentence
      // over the class-name-prefixed `message`.
      const definitionError = (errorCodes as ErrorEntity[] | undefined)?.find(
        (e) => e.errorCode === WORKFLOW_DEFINITION_INVALID_CODE
      )?.errorMessage;
      if (definitionError || message) {
        return definitionError || message;
      }
    }
  }
  return DEFAULT_ERROR_MESSAGE;
}
